import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TransactionCategory, useBillingInfo } from '@shipstation/financial-services-ui-components';

import { noop } from 'ui-components/default-props/noop';

import { SHIPMENT_VOLUME_PAYMENT_METHOD_CHANGED } from '../../../constants/analytics-events';
import { updatePaymentMethod } from '../../../financial-services-ui/api';
import { useFsuiContext } from '../../../financial-services-ui/fsui-context-provider';
import { getBillingInfoFromPaymentMethod, mapBillingInfoDataToBillingAddress } from '../../../financial-services-ui/mapping';
import { showGenericErrorNotification, showGenericSuccessNotification } from '../../../financial-services-ui/notifications';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import { useLocale } from '../../../intl/hooks';
import { useDeprecatedLocalStorage, PURCHASE_SHIPMENT_VOLUME_DATA } from '../../../hooks/use-local-storage';
import { useTrackShipmentVolumeEvent } from './hooks/use-track-shipment-volume-event';

import type { BillingAddress, PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';
import type { AdyenCheckoutData, StoredPaymentMethod, PaymentMethod } from '@shipstation/financial-services-ui-components';

export type SellerBillingFields = {
  name?: string;
  homeCountry?: string;
  phone?: string;
  companyName?: string;
};

type UsePaymentMethodChangeParams = {
  selectedQuantity: number | null;
  onDone: () => void;
};

export const usePaymentMethodChange = ({ selectedQuantity, onDone }: UsePaymentMethodChangeParams) => {
  const { locale } = useLocale();
  const seller = useSelector((state: { seller: SellerBillingFields }) => state.seller);
  const { homeCountry, companyName, phone, name } = seller ?? {};

  const { paymentMethodDetails, reloadData } = usePaymentAndSubscriptionMegaContext();
  const { state, currencyCode, preloadedSession, isPreloadingSession, onPaymentCompleted, onError, createPaymentSession } =
    useFsuiContext();
  const { billingInfo, setBillingInfo } = useBillingInfo();
  const billingInfoRef = useRef(billingInfo);
  billingInfoRef.current = billingInfo;
  const trackShipmentVolumeEvent = useTrackShipmentVolumeEvent();

  const [, setPurchaseShipmentVolumeData] = useDeprecatedLocalStorage(PURCHASE_SHIPMENT_VOLUME_DATA, undefined);

  const subscriptionPaymentMethod = useMemo(
    () =>
      paymentMethodDetails?.find((method: PaymentMethodDetails) => method.assignedPreferences?.includes(TransactionCategory.Subscription)),
    [paymentMethodDetails],
  );

  const [isAddingNew, setIsAddingNew] = useState(() => (paymentMethodDetails?.length ?? 0) === 0);
  const [paymentType, setPaymentType] = useState<PaymentMethod | null>(null);
  const [fsuiSubmitFn, setFsuiSubmitFn] = useState<() => void>(() => noop);
  const [performFormValidation, setPerformFormValidation] = useState<() => void>(() => noop);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setBillingInfoForMethod = useCallback(
    (method: PaymentMethodDetails | null | undefined) => {
      if (!method) {
        setBillingInfo({ countryCode: homeCountry }, false);
        return;
      }
      setBillingInfo(getBillingInfoFromPaymentMethod(method, { name, homeCountry, phone, companyName }) ?? undefined, true);
    },
    [setBillingInfo, homeCountry, name, companyName, phone],
  );

  const toggleAddingNew = useCallback(() => {
    setBillingInfoForMethod(isAddingNew ? subscriptionPaymentMethod : null);
    setIsAddingNew((prev) => !prev);
  }, [isAddingNew, setBillingInfoForMethod, subscriptionPaymentMethod]);

  const handleSetFsuiSubmitFn = useCallback((submit: (() => void) | null) => {
    setFsuiSubmitFn(() => submit ?? noop);
  }, []);

  const handleSetPerformFormValidation = useCallback((validationFn: (() => void) | null) => {
    setPerformFormValidation(() => validationFn ?? noop);
  }, []);

  const selectSavedMethod = useCallback(
    (method: StoredPaymentMethod | null) => {
      if (!method) {
        return;
      }
      setIsSubmitting(true);
      updatePaymentMethod({
        paymentMethodId: method.id,
        assignedPreferences: [TransactionCategory.Subscription],
        currencyCode,
      })
        .then((result) => {
          if (!result.success) {
            throw new Error(result.errorMessage ?? undefined);
          }
          trackShipmentVolumeEvent(SHIPMENT_VOLUME_PAYMENT_METHOD_CHANGED, { changeType: 'savedMethod' });
          showGenericSuccessNotification();
          reloadData();
          onDone();
        })
        .catch(() => {
          showGenericErrorNotification();
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [currencyCode, reloadData, onDone, trackShipmentVolumeEvent],
  );

  const handleNewMethodSuccess = useCallback(() => {
    setIsSubmitting(true);
    updatePaymentMethod({
      assignedPreferences: [TransactionCategory.Subscription],
      currencyCode,
      sessionId: preloadedSession?.auctanePaySessionId,
      infoUpdateRequest: billingInfoRef.current
        ? { billingAddress: mapBillingInfoDataToBillingAddress(billingInfoRef.current) as BillingAddress }
        : undefined,
    })
      .then((result) => {
        if (!result.success) {
          throw new Error(result.errorMessage ?? undefined);
        }
        trackShipmentVolumeEvent(SHIPMENT_VOLUME_PAYMENT_METHOD_CHANGED, { changeType: 'newMethod', paymentType });
        showGenericSuccessNotification();
        reloadData();
        onDone();
      })
      .catch(() => {
        showGenericErrorNotification();
        // Adyen sessions are single-use and were already consumed by the successful auth;
        // a retry needs a fresh one.
        createPaymentSession(true).catch(noop);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    currencyCode,
    preloadedSession?.auctanePaySessionId,
    reloadData,
    onDone,
    createPaymentSession,
    paymentType,
    trackShipmentVolumeEvent,
  ]);

  const handleError = useCallback(() => {
    onError(new Error('Payment method creation failed'));
  }, [onError]);

  const handlePaymentCompleted = useCallback(
    (result: unknown) => {
      onPaymentCompleted(result as Record<string, unknown>, handleNewMethodSuccess, handleError);
    },
    [onPaymentCompleted, handleNewMethodSuccess, handleError],
  );

  const handleBeforeRedirect = useCallback(async () => {
    setPurchaseShipmentVolumeData({
      updatePayload: {
        assignedPreferences: [TransactionCategory.Subscription],
        currencyCode,
        sessionId: preloadedSession?.auctanePaySessionId,
      },
      quantity: selectedQuantity,
    });
  }, [setPurchaseShipmentVolumeData, currencyCode, preloadedSession?.auctanePaySessionId, selectedQuantity]);

  const submitNewMethod = useCallback(() => {
    performFormValidation?.();
    fsuiSubmitFn?.();
  }, [performFormValidation, fsuiSubmitFn]);

  const creditCardConfig = useMemo(
    () => ({
      currencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      // Pass the already-resolved session so CreditCard doesn't self-fetch its own via getSessionId, which is a bad experience in practice
      session: (preloadedSession as AdyenCheckoutData | null) ?? undefined,
      submitPayment: handleSetFsuiSubmitFn,
      transactionCategory: TransactionCategory.Subscription,
    }),
    [currencyCode, locale, handleError, handlePaymentCompleted, preloadedSession, handleSetFsuiSubmitFn],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      beforeRedirect: handleBeforeRedirect,
      currencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop,
      returnUrl: window.location.href,
      submitPayment: handleSetFsuiSubmitFn,
      transactionCategory: TransactionCategory.Subscription,
    }),
    [handleBeforeRedirect, currencyCode, locale, preloadedSession, handleError, handleSetFsuiSubmitFn],
  );

  return {
    isAddingNew,
    toggleAddingNew,
    isPreloadingSession,
    hasSessionError: !!state.error,
    isSubmitting,
    paymentMethodDetails,
    subscriptionPaymentMethod,
    selectSavedMethod,
    submitNewMethod,
    paymentType,
    setPaymentType,
    creditCardConfig,
    payByBankUsConfig,
    handleSetPerformFormValidation,
    preloadedSession,
    locale,
  };
};
